import { useState, useEffect, useCallback } from "react";
import { TextField, Button } from "@mui/material";
import { Formik } from "formik";
import DataTable from "../../components/Table/DataTable";
import {
  getAllProviders,
  addProvider,
  updateProvider,
} from "../../services/providers/provider.api";
import { toast } from "../../components/Toast/Toast";
import { Tooltip, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { providersSchema } from "../../schemas/providersSchema";

export default function Providers() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchText, setSearchText] = useState("");
  const [selectedProvider, setSelectedProvider] = useState(null);

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllProviders({
        ...(searchText ? { search: searchText } : {}),
        page: page + 1,
        limit: rowsPerPage,
      });
      setProviders(response.data);
      setTotalPage(response.totalResults);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to fetch providers");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchText]);

  useEffect(() => {
    const delay = searchText ? 500 : 0;
    const debounceTimer = setTimeout(fetchProviders, delay);
    return () => clearTimeout(debounceTimer);
  }, [fetchProviders, rowsPerPage, searchText]);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true);
      if (selectedProvider?._id) {
        await updateProvider(selectedProvider._id, values);
        toast.success("Provider updated successfully");
      } else {
        await addProvider(values);
        toast.success("Provider added successfully");
      }
      resetForm();
      setSelectedProvider(null);
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to save provider");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 50,
      renderCell: (row) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => setSelectedProvider(row)}
            className="text-primary hover:bg-primary/10"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "name",
      headerName: "Provider Name",
      width: 400,
    },
    {
      field: "value",
      headerName: "Provider Value",
    },
  ];

  return (
    <div className="p-4">
      <div className="flex flex-row gap-2 w-full max-w-4xl mb-4">
        <TextField
          label="Search Provider"
          value={searchText}
          size="small"
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          sx={{ minWidth: "200px" }}
          InputLabelProps={{ shrink: true }}
        />
        <Formik
          initialValues={{
            name: selectedProvider?.name || "",
            value: selectedProvider?.value || "",
          }}
          enableReinitialize
          validationSchema={providersSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit: formikSubmit,
          }) => (
            <form
              onSubmit={formikSubmit}
              className="flex flex-row gap-2 w-full"
            >
              <TextField
                fullWidth
                label="Provider Name"
                name="name"
                value={values.name}
                size="small"
                onChange={handleChange}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Provider Value"
                name="value"
                value={values.value}
                size="small"
                onChange={handleChange}
                error={touched.value && Boolean(errors.value)}
                helperText={touched.value && errors.value}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={!values.name?.trim() || !values.value?.trim()}
                sx={{ textTransform: "none", fontWeight: "bold" }}
              >
                {selectedProvider ? "Update" : "Add"}
              </Button>
            </form>
          )}
        </Formik>
      </div>
      <DataTable
        columns={columns}
        rows={providers}
        loading={loading}
        totalCount={totalPage}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </div>
  );
}
